/**
 * Biconomy WebSocket Configuration
 *
 * WebSocket channels, topology info, and low-level subscription utilities for Biconomy.
 *
 * Subscribe format, channel names, frame shapes, and keep-alive mirror the production
 * Biconomy connector (gitlab.1wq.net/mm/111-connman → connector/biconomy/market/ws/
 * {connector,model}.go) and were verified against the live bei.biconomy.com feed.
 *
 * Protocol (CoinEx/ViaBTC JSON-RPC style):
 *   - Subscribe: {"method":"<chan>.subscribe","params":[...],"id":N}
 *       • state (ticker): params [symbol]            -> state.update ["SYM", {state}]
 *       • depth (book):   params [symbol, 50, "0"]   -> depth.update [isSnapshot, {asks,bids}, "SYM"]
 *       • deals (trades): params [symbol]            -> deals.update ["SYM", [{deal}, ...]]
 *       • kline:          params [symbol, seconds]   -> kline.update [[time_s, o, c, h, l, vol, deal, "SYM"]]
 *   - Keep-alive is CLIENT-initiated: client sends {"method":"server.ping","params":[],"id":N}
 *     and the server replies {"result":"pong"}. (This is the REVERSE of WEEX, where the
 *     server pings the client.)
 *
 * @see wss://bei.biconomy.com/ws
 */

import ReconnectingWebSocket from 'reconnecting-websocket';

import type {
  BiconomyDepthWsMessage,
  BiconomyKlineWsMessage,
  BiconomyStateWsMessage,
  BiconomyDealsWsMessage,
  BiconomyWsRequest,
  BiconomyWsStatus,
} from './types';

import type { WsChannelDescriptor, WsTopologyInfo } from '../../lib/types';

export const BICONOMY_WS_BASE_URL = 'wss://bei.biconomy.com/ws';

/**
 * Biconomy public WS channel method prefixes.
 */
export const BICONOMY_WS_STATE_CHANNEL = 'state';
export const BICONOMY_WS_DEPTH_CHANNEL = 'depth';
export const BICONOMY_WS_DEALS_CHANNEL = 'deals';
export const BICONOMY_WS_KLINE_CHANNEL = 'kline';

/**
 * Default order book depth requested over WS (levels per side). Biconomy depth.subscribe
 * params are [symbol, limit, mergeInterval]; "0" requests the finest price merge.
 */
export const BICONOMY_WS_DEPTH_LIMIT = 50;
export const BICONOMY_WS_DEPTH_MERGE = '0';

/**
 * Client keep-alive ping cadence (ms). Biconomy expects the CLIENT to ping.
 */
export const BICONOMY_WS_PING_INTERVAL_MS = 20_000;

/**
 * Canonical interval label -> kline.subscribe SECONDS value.
 */
export const BICONOMY_KLINE_SECONDS: Record<string, number> = {
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
  '1w': 604800,
};

/**
 * Biconomy WebSocket channel descriptors.
 * @see connector/biconomy/market/ws/connector.go
 */
export const BICONOMY_WS_CHANNELS: Record<string, WsChannelDescriptor> = {
  orderbook: {
    channel: 'orderbook',
    topic: 'depth',
    supportsBatching: false,
    docUrl: 'https://api.biconomy.com',
  },
  ticker: {
    channel: 'ticker',
    topic: 'state',
    supportsBatching: false,
    docUrl: 'https://api.biconomy.com',
  },
  trades: {
    channel: 'trades',
    topic: 'deals',
    supportsBatching: false,
    docUrl: 'https://api.biconomy.com',
  },
  klines: {
    channel: 'klines',
    topic: 'kline',
    supportsBatching: false,
    docUrl: 'https://api.biconomy.com',
  },
};

/**
 * Biconomy WebSocket topology information.
 */
export const BICONOMY_WS_TOPOLOGY: WsTopologyInfo = {
  baseUrl: BICONOMY_WS_BASE_URL,
  singleConnectionPerChannel: false,
  heartbeatIntervalMs: BICONOMY_WS_PING_INTERVAL_MS,
  note: 'CoinEx/ViaBTC JSON-RPC style. Subscribe {"method":"<chan>.subscribe","params":[...],"id":N}. Client must ping: {"method":"server.ping","params":[],"id":N} -> {"result":"pong"}.',
};

// ========================================
// WebSocket Subscription Core
// ========================================

let requestId = 0;
const nextId = (): number => {
  requestId += 1;
  return requestId;
};

/**
 * Creates a Biconomy WebSocket subscription for a single channel.
 *
 * Sends the subscribe request on open, runs a client-side keep-alive ping, and forwards
 * only frames whose `method` matches `expectedMethod`. Subscription acks and pongs (which
 * carry `result` but no `method`) are ignored. The returned socket's `close()` is wrapped
 * to also stop the ping timer.
 */
function createBiconomyWsSubscription<T>(params: {
  channel: string;
  subscribeParams: Array<string | number>;
  expectedMethod: string;
  onMessage: (data: T) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BiconomyWsStatus) => void;
}): ReconnectingWebSocket {
  const { channel, subscribeParams, expectedMethod, onMessage, onError, onStatusChange } = params;

  const ws = new ReconnectingWebSocket(BICONOMY_WS_BASE_URL, [], {
    maxRetries: 10,
    minReconnectionDelay: 1000,
    maxReconnectionDelay: 30000,
    reconnectionDelayGrowFactor: 1.5,
    connectionTimeout: 10000,
  });

  const sendPing = () => {
    if (ws.readyState === WebSocket.OPEN) {
      const ping: BiconomyWsRequest = { method: 'server.ping', params: [], id: nextId() };
      ws.send(JSON.stringify(ping));
    }
  };

  const pingTimer = setInterval(sendPing, BICONOMY_WS_PING_INTERVAL_MS);

  ws.addEventListener('open', () => {
    onStatusChange?.('connected');

    const subscribeMessage: BiconomyWsRequest = {
      method: `${channel}.subscribe`,
      params: subscribeParams,
      id: nextId(),
    };
    ws.send(JSON.stringify(subscribeMessage));
  });

  ws.addEventListener('close', () => {
    onStatusChange?.('disconnected');
  });

  ws.addEventListener('error', () => {
    onStatusChange?.('error');
    onError?.(new Error('WebSocket connection error'));
  });

  ws.addEventListener('message', event => {
    try {
      const message = JSON.parse(event.data);

      // Acks and pongs carry no `method` (only `result`/`id`) — ignore them.
      if (!message || typeof message.method !== 'string') {
        return;
      }

      // Forward only this subscription's data frames.
      if (message.method === expectedMethod) {
        onMessage(message as T);
      }
    } catch (error) {
      onError?.(error instanceof Error ? error : new Error('Failed to parse message'));
    }
  });

  // Ensure the keep-alive timer is cleared when the consumer closes the socket.
  const originalClose = ws.close.bind(ws);
  ws.close = (...args: Parameters<typeof originalClose>) => {
    clearInterval(pingTimer);
    originalClose(...args);
  };

  return ws;
}

// ========================================
// WebSocket Subscription Functions
// ========================================

/**
 * Subscribe to the Biconomy orderbook (depth) WebSocket.
 */
export function subscribeBiconomyOrderbook(params: {
  symbol: string;
  depth?: number;
  onMessage: (data: BiconomyDepthWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BiconomyWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, depth, onMessage, onError, onStatusChange } = params;
  return createBiconomyWsSubscription<BiconomyDepthWsMessage>({
    channel: BICONOMY_WS_DEPTH_CHANNEL,
    subscribeParams: [symbol, depth ?? BICONOMY_WS_DEPTH_LIMIT, BICONOMY_WS_DEPTH_MERGE],
    expectedMethod: 'depth.update',
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to the Biconomy ticker (24h state) WebSocket.
 */
export function subscribeBiconomyTicker(params: {
  symbol: string;
  onMessage: (data: BiconomyStateWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BiconomyWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBiconomyWsSubscription<BiconomyStateWsMessage>({
    channel: BICONOMY_WS_STATE_CHANNEL,
    subscribeParams: [symbol],
    expectedMethod: 'state.update',
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to the Biconomy public trades (deals) WebSocket.
 */
export function subscribeBiconomyTrades(params: {
  symbol: string;
  onMessage: (data: BiconomyDealsWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BiconomyWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, onMessage, onError, onStatusChange } = params;
  return createBiconomyWsSubscription<BiconomyDealsWsMessage>({
    channel: BICONOMY_WS_DEALS_CHANNEL,
    subscribeParams: [symbol],
    expectedMethod: 'deals.update',
    onMessage,
    onError,
    onStatusChange,
  });
}

/**
 * Subscribe to the Biconomy kline WebSocket.
 * `intervalSeconds` is the integer seconds value expected by kline.subscribe.
 */
export function subscribeBiconomyKlines(params: {
  symbol: string;
  intervalSeconds: number;
  onMessage: (data: BiconomyKlineWsMessage) => void;
  onError?: (error: Error) => void;
  onStatusChange?: (status: BiconomyWsStatus) => void;
}): ReconnectingWebSocket {
  const { symbol, intervalSeconds, onMessage, onError, onStatusChange } = params;
  return createBiconomyWsSubscription<BiconomyKlineWsMessage>({
    channel: BICONOMY_WS_KLINE_CHANNEL,
    subscribeParams: [symbol, intervalSeconds],
    expectedMethod: 'kline.update',
    onMessage,
    onError,
    onStatusChange,
  });
}
